import { Alert, Box, Stack, Typography } from "@mui/material";
import { useMemo } from "react";

import type { ProjectActorVoiceListItemDto } from "Infrastructure/Api/Api";
import { speakerColors } from "Infrastructure/Themes/Colors";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import {
  isMediaSegment,
  isPauseSegment,
  isVoiceSegment,
  useScriptEditor,
} from "Projects/Components/ScriptEditor/ScriptEditorContext";
import type { ScriptValidationError } from "../../Utils/validateScript";
import { MediaSegmentNode } from "./Nodes/MediaSegmentNode";
import { PauseSegmentNode } from "./Nodes/PauseSegmentNode";
import { VoiceSegmentNode } from "./Nodes/VoiceSegmentNode";
import { ReactionEditModal } from "./ReactionEditModal";
import { ScriptEditorToolbar } from "./ScriptEditorToolbar";
import { SegmentAddButton } from "./SegmentAddButton";

const EMPTY_MEDIA_FILES: Record<string, string> = {};

const ACTOR_COLORS = speakerColors;

interface ScriptEditorProps {
  actorVoices?: ProjectActorVoiceListItemDto[];
  validationErrors?: ScriptValidationError[];
  mediaFileNames?: Record<string, string>;
}

/**
 * Full script editor: toolbar + flex/wrap canvas of segment nodes + the
 * reaction editor modal (rendered once here so any caller — toolbar, segment
 * chip, sidebar "Upravit reakci" — can open it via the context).
 */
export const ScriptEditor = ({
  actorVoices,
  validationErrors = [],
  mediaFileNames,
}: ScriptEditorProps): React.ReactNode => {
  const { t } = useResource();
  const {
    script,
    setSelectedSegmentIndex,
    reactionEditor,
    closeReactionEditor,
    updateReactionSpan,
    removeReactionSpan,
  } = useScriptEditor();

  const segments = script.segments;

  const handleCanvasClick = () => {
    setSelectedSegmentIndex(null);
  };

  const isEmpty = segments.length === 0;

  const mediaFileNamesMap = mediaFileNames ?? EMPTY_MEDIA_FILES;

  const actorColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    (actorVoices ?? []).forEach((av, i) => {
      map[av.actorVoiceID] = ACTOR_COLORS[i % ACTOR_COLORS.length];
    });
    return map;
  }, [actorVoices]);

  const reactionModalSpan = (() => {
    if (!reactionEditor) return null;
    const seg = script.segments[reactionEditor.segmentIndex];
    if (!seg || !isVoiceSegment(seg)) return null;
    return seg.spans[reactionEditor.spanIndex] ?? null;
  })();

  return (
    <Stack spacing={2}>
      <ScriptEditorToolbar actorVoices={actorVoices} />

      <Box
        onClick={handleCanvasClick}
        sx={(theme) => ({
          minHeight: "220px",
          border: `1px solid ${theme.colors.border}`,
          borderRadius: "6px",
          padding: theme.spacing(1.5),
          display: "flex",
          flexWrap: "wrap",
          alignContent: "flex-start",
          alignItems: "center",
          gap: 1,
          cursor: "default",
          backgroundColor: theme.colors.backgroundInput,
          overflowY: "auto",
          maxHeight: "520px",
        })}
      >
        {isEmpty ? (
          <Typography
            sx={{
              color: "text.disabled",
              fontSize: "13px",
              m: "auto",
              alignSelf: "center",
            }}
          >
            {t(Resources.JobDetail.ScriptEditorEmptyState)}
          </Typography>
        ) : (
          <>
            <SegmentAddButton afterIndex={-1} />

            {segments.map((seg, i) => (
              <Box
                // biome-ignore lint/suspicious/noArrayIndexKey: segment order is the identity
                key={i}
                sx={{ display: "inline-flex", alignItems: "flex-start" }}
              >
                {isVoiceSegment(seg) && (
                  <VoiceSegmentNode
                    index={i}
                    segment={seg}
                    actorVoices={actorVoices}
                    validationErrors={validationErrors}
                    actorColor={
                      actorColorMap[seg.actorVoiceID] ?? ACTOR_COLORS[0]
                    }
                  />
                )}
                {isPauseSegment(seg) && (
                  <PauseSegmentNode
                    index={i}
                    segment={seg}
                    validationErrors={validationErrors}
                  />
                )}
                {isMediaSegment(seg) && (
                  <MediaSegmentNode
                    index={i}
                    segment={seg}
                    mediaFileNames={mediaFileNamesMap}
                    validationErrors={validationErrors}
                  />
                )}
                <SegmentAddButton afterIndex={i} />
              </Box>
            ))}
          </>
        )}
      </Box>

      {validationErrors.length > 0 && (
        <Alert
          severity="error"
          variant="outlined"
          sx={{
            fontSize: "12px",
            py: 0.5,
            borderColor: "error.dark",
            backgroundColor: "transparent",
            color: "error.main",
          }}
        >
          {t(Resources.JobDetail.ScriptErrors, {
            count: validationErrors.length,
          })}
        </Alert>
      )}

      <ReactionEditModal
        isOpen={reactionEditor !== null}
        initialValue={reactionModalSpan}
        onConfirm={(patch) => {
          if (!reactionEditor) return;
          const hasAny =
            patch.reaction != null ||
            patch.reactionVolumeEnergy != null ||
            patch.reactionPacingRhythm != null ||
            (patch.customReaction != null && patch.customReaction.length > 0);
          if (!hasAny) {
            // Saving with nothing picked → treat as remove.
            removeReactionSpan(
              reactionEditor.segmentIndex,
              reactionEditor.spanIndex,
            );
          } else {
            updateReactionSpan(
              reactionEditor.segmentIndex,
              reactionEditor.spanIndex,
              patch,
            );
          }
          closeReactionEditor();
        }}
        onCancel={() => {
          if (!reactionEditor) return;
          // For a newly-inserted pristine reaction, cancel removes it so the
          // user isn't left with an empty chip.
          if (reactionEditor.isNew && reactionModalSpan !== null) {
            const s = reactionModalSpan;
            const pristine =
              !s.reaction &&
              !s.reactionVolumeEnergy &&
              !s.reactionPacingRhythm &&
              !s.customReaction;
            if (pristine) {
              removeReactionSpan(
                reactionEditor.segmentIndex,
                reactionEditor.spanIndex,
              );
            }
          }
          closeReactionEditor();
        }}
      />
    </Stack>
  );
};
